import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import UsersApi from '../api/UsersApi'
import { useUserContext } from './UserContext'
import {
  normalizeAllowedSiteIds,
  setActiveScope,
  writeScopeByUserId
} from '../utils/siteScope'
import { FIXED_SITE_ID } from '../config/siteUi'

const SiteScopeContext = React.createContext(null)

const EMPTY_SCOPE = {
  mode: 'single',
  selectedSiteId: FIXED_SITE_ID,
  allowedSiteIds: [FIXED_SITE_ID],
  ready: false,
  userId: null,
  typeId: null
}

export const SiteScopeProvider = ({ children }) => {
  const { getSessionUser } = useUserContext()
  const sessionUser = getSessionUser()

  const [state, setState] = useState(EMPTY_SCOPE)

  useEffect(() => {
    let alive = true

    const init = async () => {
      if (!sessionUser?.id) {
        setState(EMPTY_SCOPE)
        setActiveScope(null)
        return
      }

      try {
        const ctx = await UsersApi.getMeContext()
        if (!alive) return

        const userId = Number(ctx?.userId || sessionUser.id)
        const typeId = Number(ctx?.typeId || sessionUser.typeId || 0)
        const allowedSiteIds = normalizeAllowedSiteIds([FIXED_SITE_ID])

        const next = {
          mode: 'single',
          selectedSiteId: FIXED_SITE_ID,
          allowedSiteIds,
          userId,
          typeId,
          ready: true
        }

        setState(next)
        writeScopeByUserId(userId, next)
        setActiveScope(next)
      } catch (error) {
        if (!alive) return
        const fallback = {
          mode: 'single',
          selectedSiteId: FIXED_SITE_ID,
          allowedSiteIds: normalizeAllowedSiteIds([FIXED_SITE_ID]),
          userId: Number(sessionUser.id),
          typeId: Number(sessionUser.typeId || 0),
          ready: true
        }
        setState(fallback)
        writeScopeByUserId(fallback.userId, fallback)
        setActiveScope(fallback)
      }
    }

    init()
    return () => {
      alive = false
    }
  }, [sessionUser?.id, sessionUser?.siteId, sessionUser?.typeId])

  const setMode = useCallback(() => {
    setState((prev) => {
      const next = {
        ...prev,
        mode: 'single',
        selectedSiteId: FIXED_SITE_ID,
        allowedSiteIds: normalizeAllowedSiteIds([FIXED_SITE_ID])
      }
      if (next?.ready) {
        if (next?.userId) {
          writeScopeByUserId(next.userId, next)
        }
        setActiveScope(next)
      }
      return next
    })
  }, [])

  const setSelectedSiteId = useCallback(() => {
    setState((prev) => {
      const next = {
        ...prev,
        mode: 'single',
        selectedSiteId: FIXED_SITE_ID,
        allowedSiteIds: normalizeAllowedSiteIds([FIXED_SITE_ID])
      }
      if (next?.ready) {
        if (next?.userId) {
          writeScopeByUserId(next.userId, next)
        }
        setActiveScope(next)
      }
      return next
    })
  }, [])

  useEffect(() => {
    if (!state?.ready) return
    if (state?.userId) {
      writeScopeByUserId(state.userId, state)
    }
    setActiveScope(state)
  }, [state])

  const value = useMemo(() => ({
    mode: state.mode,
    selectedSiteId: state.selectedSiteId,
    allowedSiteIds: state.allowedSiteIds,
    ready: state.ready,
    userId: state.userId,
    typeId: state.typeId,
    setMode,
    setSelectedSiteId
  }), [state, setMode, setSelectedSiteId])

  return <SiteScopeContext.Provider value={value}>{children}</SiteScopeContext.Provider>
}

SiteScopeProvider.propTypes = {
  children: PropTypes.node
}

export const useSiteScope = () => {
  return useContext(SiteScopeContext) || EMPTY_SCOPE
}
